import {
  buildOutcomeGreenRateLabel,
  getDefaultOutcomeResultLabel,
  getOutcomeNoun,
  getOutcomePluralNoun,
  greenRedYellowResultTypeCategory,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import {
  SlideoverDialog,
  SlideoverDialogButtons,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { type FC, useState } from "react";

interface OutcomeLabelsEditorSlideoverDialogProps {
  outcome: OutcomeDefinition;
  onSave: (outcome: OutcomeDefinition) => void;
}

/**
 * Content for the "Edit outcome terminology" slideover. Renders one text field
 * per outcome value so users can rename the result labels on this playbook,
 * plus a "Labels" section for overriding the noun and conversion-metric labels
 * used for the positive (green) result across dashboards, charts, tables,
 * summaries, and coaching views. Mounts fresh whenever the slideover opens (the
 * container unmounts it on close), so the draft state is seeded from props at
 * mount with no effect-based reset.
 */
export const OutcomeLabelsEditorSlideoverDialog: FC<
  OutcomeLabelsEditorSlideoverDialogProps
> = ({ outcome, onSave }) => {
  // Keyed by result value (e.g. "win_loss:won"), which is unique per outcome.
  const [labels, setLabels] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      outcome.results.map((result) => [result.value, result.label]),
    ),
  );
  // Noun/rate overrides default to the saved values (empty when unset, so the
  // field falls back to the derived placeholder default).
  const [singularNoun, setSingularNoun] = useState(
    () => outcome.singular_noun ?? "",
  );
  const [pluralNoun, setPluralNoun] = useState(() => outcome.plural_noun ?? "");
  const [rateLabel, setRateLabel] = useState(() => outcome.rate_label ?? "");

  // Placeholders show the default that would apply if the field were left
  // blank. They ignore the saved override (so the field can fall back to the
  // built-in default) but track the in-progress singular noun so the plural and
  // rate previews stay in sync as the user types.
  const baseDefaultsOutcome: OutcomeDefinition = {
    ...outcome,
    singular_noun: undefined,
    plural_noun: undefined,
    rate_label: undefined,
  };
  const previewOutcome: OutcomeDefinition = {
    ...baseDefaultsOutcome,
    singular_noun: singularNoun.trim() || undefined,
  };
  const singularNounPlaceholder = getOutcomeNoun(baseDefaultsOutcome);
  const pluralNounPlaceholder = getOutcomePluralNoun(previewOutcome);
  const rateLabelPlaceholder = buildOutcomeGreenRateLabel(previewOutcome);

  const handleSave = (close: () => void) => {
    // A blank field falls back to the preset default rather than erroring; for
    // custom outcomes (no preset) it keeps the existing label. Either way the
    // resulting label is non-empty, satisfying the outcome result schema.
    const results = outcome.results.map((result) => ({
      ...result,
      label:
        labels[result.value]?.trim() ||
        getDefaultOutcomeResultLabel(outcome.id, result.value) ||
        result.label,
    }));

    // A blank noun/rate override clears it (falls back to the derived default).
    onSave({
      ...outcome,
      results,
      singular_noun: singularNoun.trim() || undefined,
      plural_noun: pluralNoun.trim() || undefined,
      rate_label: rateLabel.trim() || undefined,
    });
    close();
  };

  return (
    <SlideoverDialog label="Edit outcome terminology">
      {({ close }) => (
        <>
          <SlideoverDialogTitleHeader isLoading={false}>
            Edit outcome terminology
          </SlideoverDialogTitleHeader>

          <SlideoverDialogContent>
            <div className="space-y-6 p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Status names
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Each customer interaction will be classified as one of the
                    following statuses.
                  </p>
                </div>

                <div className="space-y-4">
                  {outcome.results.map((result) => {
                    const defaultLabel =
                      getDefaultOutcomeResultLabel(outcome.id, result.value) ??
                      result.label;
                    // The N/A (gray) result is the not-applicable bucket
                    // excluded from outcome metrics; keep its field visible but
                    // read-only so its label can't be customized.
                    const isNotApplicable =
                      result.category === greenRedYellowResultTypeCategory.gray;
                    // Wrap each Input in its own element so the container's
                    // space-y-4 only separates fields — Input renders a
                    // fragment, so without this the gap would also push between
                    // each field's label and its input.
                    return (
                      <div key={result.value}>
                        <Input
                          label={defaultLabel}
                          value={labels[result.value] ?? ""}
                          placeholder={defaultLabel}
                          isDisabled={isNotApplicable}
                          onChange={(event) =>
                            setLabels((prev) => ({
                              ...prev,
                              [result.value]: event.target.value,
                            }))
                          }
                          maxLength={100}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Labels
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    These labels control how the outcome statistics appear in
                    dashboards, charts, tables, summaries, and coaching views.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Input
                      label="Singular noun"
                      value={singularNoun}
                      placeholder={singularNounPlaceholder}
                      description='The noun used when referring to one positive outcome result. For example, "win".'
                      onChange={(event) => setSingularNoun(event.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <Input
                      label="Plural noun"
                      value={pluralNoun}
                      placeholder={pluralNounPlaceholder}
                      description='The noun used when referring to multiple positive outcome results. For example, "wins".'
                      onChange={(event) => setPluralNoun(event.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <Input
                      label="Conversion metric label"
                      value={rateLabel}
                      placeholder={rateLabelPlaceholder}
                      description='The label for the positive outcome conversion metric. For example, "Win rate".'
                      onChange={(event) => setRateLabel(event.target.value)}
                      maxLength={100}
                    />
                  </div>
                </div>
              </div>
            </div>
          </SlideoverDialogContent>

          <SlideoverDialogButtons>
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.MEDIUM}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              size={Size.MEDIUM}
              onClick={() => handleSave(close)}
            >
              Save
            </Button>
          </SlideoverDialogButtons>
        </>
      )}
    </SlideoverDialog>
  );
};
